// Resolves the polygon ring / line points backing an active `vertexEdit`,
// mirroring `FullscreenMapCanvas`'s own `vertexTarget`/`vertexPts`/`vertexColor`
// lookup (`MapEditorPage.tsx:3265-3276`, `drawnColour` at line 197). Pure
// function only — no React, no I/O, same shape as `editorGroundGeoJson.ts`.

import { resolveHuntBorderColor, SUBAREA_STROKE, FORBIDDEN_STROKE } from '../../lib/mapBorders'
import { LINE_DEFAULT_COLOR } from '../../lib/mapLines'
import { MIN_RING_POINTS } from '../../lib/polygonUnion'
import type { GeoArea, GeoPt } from '../../lib/types'
import type { EditState, VertexEdit } from './mapLibreEditorTypes'

export interface VertexEditTarget {
  kind: 'polygon' | 'line'
  points: GeoPt[]
  /** The accent stroke a `VertexEditSession` styles the lifted feature with —
   *  the SAME colour the static layer would have painted it in (team → own →
   *  default precedence for a border; the fixed layer colour for a sub-area
   *  or forbidden area; the line's own stored colour, falling back to the
   *  app default). */
  stroke: string
  /** Translucent fill for a lifted polygon — the stroke plus a fixed `"44"`
   *  alpha suffix. `null` for a hunt border: a saved border is drawn as lines
   *  only, so a wash while reshaping it previewed a filled area the map never
   *  shows once saved. Lines carry no fill; `VertexEditSession`'s own line
   *  styling is stroke-only. */
  fill?: string | null
}

function findAreaById(items: readonly GeoArea[], id: string): GeoArea | undefined {
  return items.find((a) => a.id === id)
}

/**
 * Whether the editor may reshape this area's outline — the single gate in front of
 * vertex editing, asked by both the pencil on the layer list and a click on the
 * polygon itself.
 *
 * Four shapes are read-only, and the last two matter because what this admits is
 * handed to `VertexEditSession`, which asserts its preconditions and THROWS when they
 * do not hold. Nothing catches that between there and the router, so geometry wrongly
 * admitted here does not degrade — it replaces the whole map page with the router's
 * error screen.
 *
 *  - An outline stored as an imported blob. The API refuses a geometry write for it,
 *    so vertex handles would offer an edit that always fails.
 *  - An outline with more than one ring — several parcels, or a parcel with an
 *    excluded lake. Dragging a vertex would have to pick one ring and discard the
 *    rest on save.
 *  - A ring with too few points to be a polygon at all. Degenerate outlines are real
 *    stored data: the API rejects a write for one, and the map layers already filter
 *    them out of everything they draw.
 *  - A ring stored CLOSED, its first point repeated at the end. The editor works in
 *    open rings and `dtoToGeoArea` opens every ring it reads, so this should no longer
 *    be reachable — it stays as the backstop for any producer that does not come
 *    through that mapper, because a closed ring doubles terra-draw's seam and puts
 *    every vertex index it reports one off from the list the editor is holding.
 *
 * Renaming, recolouring and deleting stay available for all four: they carry the
 * geometry back untouched, which is exactly the write the API preserves.
 */
export function geometryEditable(a: { rings: GeoPt[][]; coordinateSource?: string }): boolean {
  if (a.coordinateSource === 'storage') return false
  if (a.rings.length !== 1) return false
  const ring = a.rings[0]
  if (ring.length < MIN_RING_POINTS) return false
  const first = ring[0]
  const last = ring[ring.length - 1]
  return !(first.lat === last.lat && first.lon === last.lon)
}

/**
 * Resolves the shape backing `vertexEdit`, or `null` when there is no active
 * vertex edit — or the target id no longer exists in `state`, mirroring the
 * old canvas's own `.find()` returning `undefined` (silently rendering
 * nothing, the same way a stale `vertexEdit` referencing a since-deleted
 * shape did there).
 *
 * THROWS when a resolved polygon target does not have exactly one ring.
 * `geometryEditable()` (`MapEditorPage.tsx:145`) is the upstream gate that
 * is supposed to keep a multi-ring or storage-sourced area from ever
 * reaching `vertexEdit` in the first place — this is the last-resort
 * assertion the plan calls for ("assert anyway and THROW on violation,
 * never corrupt"): a bypassed precondition must fail loudly here rather than
 * silently handing `VertexEditSession` (and this canvas's own suppression
 * logic) a shape it was never built to edit.
 */
export function resolveVertexEditTarget(
  state: EditState,
  vertexEdit: VertexEdit,
  teamColor: string | undefined,
): VertexEditTarget | null {
  if (!vertexEdit) return null

  if (vertexEdit.kind === 'line') {
    const line = (state.lines ?? []).find((l) => l.id === vertexEdit.id)
    if (!line) return null
    return { kind: 'line', points: line.points, stroke: line.color ?? LINE_DEFAULT_COLOR }
  }

  const area =
    vertexEdit.kind === 'border'
      ? findAreaById(state.borders, vertexEdit.id)
      : vertexEdit.kind === 'subarea'
        ? findAreaById(state.subAreas, vertexEdit.id)
        : findAreaById(state.forbiddenAreas, vertexEdit.id)
  if (!area) return null

  if (area.rings.length !== 1) {
    throw new Error(
      `MapLibreEditorCanvas: vertex-edit target '${area.id}' (${vertexEdit.kind}) has ${area.rings.length} rings — ` +
        'only a single-ring area can be lifted for vertex editing. geometryEditable() is supposed to gate this ' +
        'upstream (multi-ring / storage-sourced areas never offer the vertex-edit affordance); reaching this with ' +
        'more than one ring means that gate was bypassed.',
    )
  }

  const stroke =
    vertexEdit.kind === 'border'
      ? resolveHuntBorderColor({ areaColor: area.color, teamColor })
      : vertexEdit.kind === 'subarea'
        ? SUBAREA_STROKE
        : FORBIDDEN_STROKE
  return { kind: 'polygon', points: area.rings[0], stroke, fill: vertexEdit.kind === 'border' ? null : `${stroke}44` }
}
